import {
  BookOpen,
  Brain,
  Calculator,
  CheckSquare,
  Ear,
  Eye,
  MessageCircle,
  Move,
  PenTool,
  Sparkles,
  Waves,
  Zap,
} from 'lucide-react';
import type { NeuroOption } from 'types/neuro';

export const neuroProfiles: NeuroOption[] = [
  {
    id: 'autism',
    name: 'Autism',
    description: 'Visual routines, transition timers, communication choices, social stories, and calm sensory support.',
    longDescription: 'A calm, visual and predictable learning space that adapts to how each child communicates, learns and feels.',
    colorClass: 'bg-blue-50 text-blue-900 border-blue-300 hover:bg-blue-100',
    icon: Brain,
    theme: {
      primary: 'blue',
      secondary: 'indigo',
      background: 'bg-blue-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-blue-500',
    },
    learningStyle: 'Visual • Predictable • Sensory-safe',
  },
  {
    id: 'adhd',
    name: 'ADHD',
    description: 'Focus timers, movement breaks, rewards, reminders, and support for energy, attention and follow-through.',
    longDescription: 'Dynamic learning with short steps, movement-friendly pacing, focus support and positive momentum.',
    colorClass: 'bg-yellow-50 text-yellow-900 border-yellow-300 hover:bg-yellow-100',
    icon: Zap,
    theme: {
      primary: 'yellow',
      secondary: 'orange',
      background: 'bg-yellow-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-yellow-500',
    },
    learningStyle: 'Focus • Move • Reset',
  },
  {
    id: 'dyslexia',
    name: 'Dyslexia',
    description: 'Read-aloud, reading overlays, friendly fonts, vocabulary help, and multisensory reading support.',
    longDescription: 'Reading support that helps learners access text through sound, visuals, spacing and confidence-safe tools.',
    colorClass: 'bg-purple-50 text-purple-900 border-purple-300 hover:bg-purple-100',
    icon: BookOpen,
    theme: {
      primary: 'purple',
      secondary: 'violet',
      background: 'bg-purple-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-purple-500',
    },
    learningStyle: 'Read • Listen • Understand',
  },
  {
    id: 'dysgraphia',
    name: 'Dysgraphia',
    description: 'Helps learners express ideas through speech, typing, guided writing, handwriting support, and AI organisation.',
    longDescription: 'A writing companion that helps learners show what they know without handwriting becoming the barrier.',
    colorClass: 'bg-green-50 text-green-900 border-green-300 hover:bg-green-100',
    icon: PenTool,
    theme: {
      primary: 'green',
      secondary: 'emerald',
      background: 'bg-green-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-green-500',
    },
    learningStyle: 'Write • Speak • Organise',
  },
  {
    id: 'dyscalculia',
    name: 'Dyscalculia',
    description: 'Visual maths, number sense tools, step-by-step working, manipulatives, and real-world examples.',
    longDescription: 'Maths support that makes numbers visible, concrete and easier to reason through one step at a time.',
    colorClass: 'bg-red-50 text-red-900 border-red-300 hover:bg-red-100',
    icon: Calculator,
    theme: {
      primary: 'red',
      secondary: 'rose',
      background: 'bg-red-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-red-500',
    },
    learningStyle: 'See • Count • Solve',
  },
  {
    id: 'dyspraxia',
    name: 'Dyspraxia / DCD',
    description: 'Movement-friendly tasks, coordination support, motor planning, handwriting alternatives, and pacing.',
    longDescription: 'Support for planning movement, organising actions and completing activities without unnecessary motor pressure.',
    colorClass: 'bg-teal-50 text-teal-900 border-teal-300 hover:bg-teal-100',
    icon: Move,
    theme: {
      primary: 'teal',
      secondary: 'cyan',
      background: 'bg-teal-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-teal-500',
    },
    learningStyle: 'Move • Plan • Practise',
  },
  {
    id: 'tourettes',
    name: 'Tourette / Tics',
    description: 'Tic-friendly pacing, no time pressure, flexible responses, calm breaks, and accepting support.',
    longDescription: 'A flexible learning space that respects tics, reduces pressure and keeps the learner included.',
    colorClass: 'bg-violet-50 text-violet-900 border-violet-300 hover:bg-violet-100',
    icon: Sparkles,
    theme: {
      primary: 'violet',
      secondary: 'purple',
      background: 'bg-violet-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-violet-500',
    },
    learningStyle: 'Flexible • Calm • Accepting',
  },
  {
    id: 'speech-language',
    name: 'Speech & Language / DLD',
    description: 'Helpful phrase cards, sentence choices and story steps. Point, read or speak at your own pace.',
    longDescription: 'Explore everyday phrases, build a sentence and sequence a short story with text and picture cues. Speaking is optional; this is educational practice, not speech therapy or assessment.',
    colorClass: 'bg-cyan-50 text-cyan-900 border-cyan-300 hover:bg-cyan-100',
    icon: MessageCircle,
    theme: {
      primary: 'cyan',
      secondary: 'blue',
      background: 'bg-cyan-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-cyan-500',
    },
    learningStyle: 'Understand • Express • Connect',
  },
  {
    id: 'auditory',
    name: 'Auditory Processing',
    description: 'Clear speech, captions, repeat buttons, visual reinforcement, and reduced listening load.',
    longDescription: 'Audio support that pairs spoken information with visuals, text and replayable instructions.',
    colorClass: 'bg-indigo-50 text-indigo-900 border-indigo-300 hover:bg-indigo-100',
    icon: Ear,
    theme: {
      primary: 'indigo',
      secondary: 'blue',
      background: 'bg-indigo-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-indigo-500',
    },
    learningStyle: 'Hear • See • Repeat',
  },
  {
    id: 'spd',
    name: 'Sensory Processing',
    description: 'Calming sounds, brightness controls, sensory breaks, softer motion, and comfort settings.',
    longDescription: 'Sensory regulation tools and calmer environments that help learners stay comfortable and ready.',
    colorClass: 'bg-pink-50 text-pink-900 border-pink-300 hover:bg-pink-100',
    icon: Waves,
    theme: {
      primary: 'pink',
      secondary: 'rose',
      background: 'bg-pink-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-pink-500',
    },
    learningStyle: 'Calm • Adjust • Regulate',
  },
  {
    id: 'visual-stress',
    name: 'Visual Stress',
    description: 'Low-glare themes, font scaling, spacing controls, overlays, and comfortable reading layouts.',
    longDescription: 'Visual comfort tools that make screens easier to look at, read and use for longer.',
    colorClass: 'bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100',
    icon: Eye,
    theme: {
      primary: 'amber',
      secondary: 'yellow',
      background: 'bg-amber-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-amber-500',
    },
    learningStyle: 'Comfort • Clarity • Control',
  },
  {
    id: 'executive-function',
    name: 'Executive Function',
    description: 'Choose a first step, review what you need and explore a change of plan at your own pace.',
    longDescription: 'Optional planning practice with small-step choices, preparation checklists and transition cards. Asking for help or more time counts; no reminders are sent and this is not clinical assessment.',
    colorClass: 'bg-slate-50 text-slate-900 border-slate-300 hover:bg-slate-100',
    icon: CheckSquare,
    theme: {
      primary: 'slate',
      secondary: 'gray',
      background: 'bg-slate-50',
      cardBg: 'bg-white/90',
      accent: 'border-l-4 border-slate-500',
    },
    learningStyle: 'Choose • Prepare • Adjust',
  },
];
